import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '../configuration/app-configuration';
import { UserAuthService } from '../auth/user-auth/user-auth.service';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, firstValueFrom, map, tap, throwError } from 'rxjs';

import { CommonApiBodyCaptureJobInvoice } from './model/commonApiBodyCaptureJobInvoice';
import { CommonApiErrorBody } from './model/commonApiErrorBody';
import { CaptureData } from './model/captureData';
import { CommonApiBodyInvoiceCapture } from './model/commonApiBodyInvoiceCapture';
import { CaptureJobInvoice } from './model/captureJobInvoice';

export type ResumeJobResponse = CommonApiBodyCaptureJobInvoice | null;
export type StartJobResponse = CommonApiBodyCaptureJobInvoice | null;
export type ApiErrorResponse = CommonApiErrorBody;

export class ApiError extends Error {
  constructor(public data: ApiErrorResponse) {
    super(`${data.error.title}\n${data.error.details}`);
  }
}

export interface ApiClient {
  getResumeJob(): Promise<ResumeJobResponse>;
  postStartJob(): Promise<StartJobResponse>;
  postCaptureJob(jobId: number, captureData: CaptureData): Promise<void>;
  postCompleteJob(jobId: number, captureData: CaptureData): Promise<void>;
}

@Injectable({
  providedIn: 'root',
})
export class ApiClientService implements ApiClient {
  private readonly apiUrl: string = '';
  private readonly apiVersion: string = 'v1';

  private readonly userAuth = inject(UserAuthService);
  private readonly httpClient = inject(HttpClient);

  constructor() {
    const appConfig = inject(APP_CONFIG);
    this.apiUrl = appConfig.api.url;
  }

  async getResumeJob(): Promise<ResumeJobResponse> {
    const accessToken = await this.userAuth.getAccessToken();
    const requestPath = `${this.apiUrl}/${this.apiVersion}/me/jobs/resume`;

    return firstValueFrom(
      this.httpClient
        .get<ResumeJobResponse>(requestPath, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          observe: 'response',
        })
        .pipe(
          catchError((response: HttpErrorResponse) => {
            console.log('Resume job error response', response);
            return throwError(() => new ApiError(response.error as ApiErrorResponse));
          }),
          map((response: HttpResponse<ResumeJobResponse>) => {
            console.log('Resume job success response', response);
            return response.body ?? null;
          }),
        ),
    );
  }

  async postStartJob(): Promise<StartJobResponse> {
    const accessToken = await this.userAuth.getAccessToken();
    const requestPath = `${this.apiUrl}/${this.apiVersion}/me/jobs/start`;

    return firstValueFrom(
      this.httpClient
        .post<StartJobResponse>(requestPath, null, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          observe: 'response',
        })
        .pipe(
          catchError((response: HttpErrorResponse) => {
            console.log('Start job error response', response);
            return throwError(() => new ApiError(response.error as ApiErrorResponse));
          }),
          map((response: HttpResponse<StartJobResponse>) => {
            console.log('Start job success response', response);
            return response.body ?? null;
          }),
        ),
    );
  }

  async postCaptureJob(jobId: number, captureData: CaptureData): Promise<void> {
    const accessToken = await this.userAuth.getAccessToken();
    const requestPath = `${this.apiUrl}/${this.apiVersion}/me/jobs/${jobId}/capture`;

    const body: CommonApiBodyInvoiceCapture = {
      data: { capture_data: captureData },
    };

    await firstValueFrom(
      this.httpClient
        .post(requestPath, body, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          observe: 'response',
        })
        .pipe(
          catchError((response: HttpErrorResponse) => {
            console.log('Capture job error response', response);
            return throwError(() => new ApiError(response.error as ApiErrorResponse));
          }),
          tap((response: HttpResponse<object>) => {
            console.log('Capture job success response', response);
          }),
        ),
    );
  }

  async postCompleteJob(jobId: number, captureData: CaptureData): Promise<void> {
    const accessToken = await this.userAuth.getAccessToken();
    const requestPath = `${this.apiUrl}/${this.apiVersion}/me/jobs/${jobId}/complete`;

    const body: CommonApiBodyInvoiceCapture = {
      data: { capture_data: captureData },
    };

    await firstValueFrom(
      this.httpClient
        .post(requestPath, body, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          observe: 'response',
        })
        .pipe(
          catchError((response: HttpErrorResponse) => {
            console.log('Complete job error response', response);
            return throwError(() => new ApiError(response.error as ApiErrorResponse));
          }),
          tap((response: HttpResponse<object>) => {
            console.log('Complete job success response', response);
          }),
        ),
    );
  }

  async getJobsByStatus(statuses: string[]): Promise<{ data: CaptureJobInvoice[] }> {
    const accessToken = await this.userAuth.getAccessToken();
    const requestPath =
      `${this.apiUrl}/${this.apiVersion}/jobs?` +
      statuses.map(status => `status=${encodeURIComponent(status)}`).join('&');

    return firstValueFrom(
      this.httpClient
        .get<{ data: CaptureJobInvoice[] }>(requestPath, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          observe: 'response',
        })
        .pipe(
          catchError((response: HttpErrorResponse) => {
            console.log('getJobsByStatus error response', response);
            return throwError(() => new ApiError(response.error as ApiErrorResponse));
          }),
          map((res: HttpResponse<{ data: CaptureJobInvoice[] }>) => res.body ?? { data: [] }),
        ),
    );
  }
}
